import React, {CSSProperties, useCallback, useMemo, useState} from "react";
import {useDropzone} from "react-dropzone";
import {Translate} from "react-redux-i18n";
import {KButton, KBUTTON_SIZE, KBUTTON_VARIANT, KSpace, KSpinner, KSpinnerSize} from "@kopjra/uikit";

export interface Props {
    onDoTimestamp: (file: File, trusted: boolean) => Promise<void>;
}

const baseStyle: CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: 'center',
    padding: "30px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#8c9ab1",
    borderStyle: "dashed",
    backgroundColor: "#ffffff",
    color: "#4E596B",
    outline: "none",
    transition: "border .24s ease-in-out",
    fontFamily: "KTextFont, sans-serif",
    fontWeight: 300,
    letterSpacing: 1.1,
    fontSize: 20,
};

const activeStyle = {
    borderColor: "#5198f4",
};

const acceptStyle = {
    borderColor: "#2bd18f",
};

const rejectStyle = {
    borderColor: "#ef615e"
};

export const TimestampFile: React.FC<Props> = ({onDoTimestamp}) => {
    const [myFile, setMyFile] = useState<File | undefined>();
    const [timestamping, setTimestamping] = useState(false);

    const onDrop = useCallback((acceptedFiles) => setMyFile(acceptedFiles[0]), []);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
    } = useDropzone({maxFiles: 1, disabled: timestamping, onDrop});

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
    }), [
        isDragActive,
        isDragReject,
        isDragAccept,
    ]);

    return (
        <section className="container">
            <div {...getRootProps({className: "dropzone", style})}>
                <input {...getInputProps()} />
                {!myFile ? (
                    <div className={"text-center"}>
                        <i className="fal fa-file-plus"/>
                        <Translate value={"interaction.dragFiles"} style={{display: "block", marginTop: 20}}/>
                    </div>
                ) : (
                    <div className={"text-center"}>
                        {timestamping ? (
                            <KSpinner size={KSpinnerSize.lg}/>
                        ) : (
                            <>
                                <i className="fal fa-file fa-2x"/>
                                <span style={{display: "block", marginTop: 20}}>{myFile.name}</span>
                                <KSpace spaces={3}/>
                                <KButton
                                    text={<><i className="fal fa-award"/> <Translate value="interaction.doTrustedTimestamp"/></>}
                                    variant={KBUTTON_VARIANT.success}
                                    size={KBUTTON_SIZE.nm}
                                    onClick={async (e) => {
                                        setTimestamping(true);
                                        try {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            await onDoTimestamp(myFile, true);
                                            setMyFile(undefined);
                                        } finally {
                                            setTimestamping(false);
                                        }
                                    }}
                                />
                                &nbsp;&nbsp;o&nbsp;&nbsp;
                                <KButton
                                    text={<><i className="fal fa-stamp"/> <Translate value="interaction.doTrustlessTimestamp"/></>}
                                    variant={KBUTTON_VARIANT.secondary}
                                    size={KBUTTON_SIZE.nm}
                                    onClick={async (e) => {
                                        setTimestamping(true);
                                        try {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            await onDoTimestamp(myFile, false);
                                            setMyFile(undefined);
                                        } finally {
                                            setTimestamping(false);
                                        }
                                    }}
                                />
                            </>
                        )}
                    </div>
                )}
                <div></div>
            </div>
        </section>
    );
};
